import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

interface VideoPlayerProps {
  videoId: string;
  autoplay?: boolean;
  className?: string;
  onClose?: () => void;
  requestFullscreenOnMount?: boolean;
}

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 aspect ratio par défaut */
  background: #000;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    /* Mobile overlay-constrained layout: between site header and bottom nav */
    position: fixed;
    top: var(--cdx-overlay-top, 0px);
    left: 0;
    right: 0;
    bottom: var(--cdx-overlay-bottom, 0px);
    width: 100vw;
    max-width: 100%;
    padding-bottom: 0;
    border-radius: 0;
    z-index: 5001;
    margin: 0;
    
    &.is-fullscreen {
      --cdx-fs-overscan: 0px; /* no overscan to keep full YouTube UI visible */
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100vw;
      height: 100dvh;
    }

  /* Portrait: light zoom; a bit more while interacting */
  &.is-fullscreen:not(.is-landscape) {
    --cdx-zoom-por: 1.08;
  }
  &.is-interact:not(.is-landscape) {
    --cdx-zoom-por: 1.12;
  }
  }

  /* Fullscreen container rules (apply on any width, including landscape > 768px) */
  &.is-fullscreen {
    --cdx-fs-overscan: 0px;
    /* define a consistent fullscreen height var for children */
    --cdx-fs-h: 100vh;
    --cdx-fs-h: 100svh;
    --cdx-fs-h: 100dvh;
    --cdx-safe-left: env(safe-area-inset-left, 0px);
    --cdx-safe-right: env(safe-area-inset-right, 0px);
    --cdx-zoom-land: 1; /* landscape zoom */
    --cdx-zoom-por: 1;  /* portrait zoom */
    --cdx-zoom: 1; /* default no zoom */
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    height: 100svh;
    height: 100dvh;
    border-radius: 0;
    z-index: 5001;
    margin: 0;
  }

  /* Landscape: slight zoom to reduce side black bars when not interacting */
  &.is-fullscreen.is-landscape {
    --cdx-zoom-land: 1.16;
  }

  /* Landscape fallback (if fullscreen isn't granted), still fill viewport */
  &.is-landscape {
    --cdx-fs-overscan: 0px;
    --cdx-fs-h: 100vh;
    --cdx-fs-h: 100svh;
    --cdx-fs-h: 100dvh;
    --cdx-zoom-land: 1.16;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    height: 100svh;
    height: 100dvh;
    border-radius: 0;
    z-index: 5001;
    margin: 0;
  }

  &.mobile-centered {
    @media (max-width: 768px) {
      position: fixed;
      top: var(--cdx-overlay-top, 0px);
      left: 0;
      right: 0;
      bottom: var(--cdx-overlay-bottom, 0px);
      width: 100vw;
      height: auto;
      max-width: 100%;
      padding-bottom: 0;
      border-radius: 0;
      z-index: 5001;
      margin: 0;
    }
  }
`;

const RotateButton = styled.button<{ $fs?: boolean; $land?: boolean }>`
  position: fixed;
  top: ${props => props.$fs ? (props.$land ? 'calc(50% + 56px)' : '8px') : 'calc(var(--cdx-overlay-top, 0px) + 8px)'};
  right: ${props => props.$land ? '8px' : '64px'}; /* near X in portrait, stacked on side in landscape */
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid #FFD700;
  background: rgba(0, 0, 0, 0.75);
  color: #FFD700;
  font-size: 22px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5003;
  box-shadow: 0 4px 10px rgba(0,0,0,0.4);
  pointer-events: auto;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.06);
  }

  @media (max-width: 768px) {
    display: flex;
  }

  /* Also show whenever the container is fullscreen, even if width > 768px */
  ${VideoContainer}.is-fullscreen & {
    display: flex;
  }
`;

const StyledIframe = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;

  /* Global fullscreen sizing: cover by height (center horizontally) */
  ${VideoContainer}.is-fullscreen & {
    /* Use block + static positioning so the wrapper can center it */
    position: static;
    display: block;
    height: var(--cdx-fs-h);
    width: max(100dvw, calc(var(--cdx-fs-h) * 1.7778));
    margin: 0 auto;
    transform: scale(var(--cdx-zoom, 1));
    transform-origin: center center;
  }

  /* Portrait fullscreen: contain by WIDTH (fill width, keep 16:9) */
  ${VideoContainer}.is-fullscreen:not(.is-landscape) & {
    position: static;
    display: block;
    width: 100vw;
    width: 100dvw;
    height: calc(100vw / 1.7778);
    height: calc(100dvw / 1.7778);
    max-height: var(--cdx-fs-h);
    margin: 0 auto;
    transform: scale(var(--cdx-zoom-por, 1));
    transform-origin: center center;
  }

  /* When interacting, temporarily contain to reveal full YouTube UI */
  ${VideoContainer}.is-interact & {
    position: static;
    display: block;
    width: 100dvw;
    height: var(--cdx-fs-h);
    margin: 0 auto;
    transform: none; /* reset zoom and keep centered */
  }

  /* Portrait interact: keep contain-by-width, not full height */
  ${VideoContainer}.is-interact:not(.is-landscape) & {
    position: static;
    display: block;
    width: 100vw;
    width: 100dvw;
    height: calc(100vw / 1.7778);
    height: calc(100dvw / 1.7778);
    max-height: var(--cdx-fs-h);
    margin: 0 auto;
    transform: scale(var(--cdx-zoom-por, 1));
    transform-origin: center center;
  }

  /* Portrait default (not fullscreen): contain by width and center */
  ${VideoContainer}:not(.is-landscape) & {
    position: static;
    display: block;
    width: 100vw;
    width: 100dvw;
    height: calc(100vw / 1.7778);
    height: calc(100dvw / 1.7778);
    max-height: 100dvh;
    margin: 0 auto;
    transform: none;
  }

  /* Option A: permanent CONTAIN in landscape (fullscreen or not) */
  ${VideoContainer}.is-fullscreen.is-landscape:not(.is-interact) &,
  ${VideoContainer}.is-landscape:not(.is-interact) & {
    position: static;
    display: block;
    aspect-ratio: 16 / 9;
    width: min(100dvw, calc(var(--cdx-fs-h) * 1.7778));
    height: auto;
    max-height: var(--cdx-fs-h);
    margin: 0 auto;
    transform: scale(var(--cdx-zoom-land, 1));
    transform-origin: center center;
  }

  @media (max-width: 768px) {
    /* Default: cover the width to avoid side gaps */
    --cdx-eff-h: calc(100vh - var(--cdx-overlay-top, 0px) - var(--cdx-overlay-bottom, 0px) + var(--cdx-fs-overscan, 0px));
    --cdx-eff-h: calc(100dvh - var(--cdx-overlay-top, 0px) - var(--cdx-overlay-bottom, 0px) + var(--cdx-fs-overscan, 0px));
    height: var(--cdx-eff-h);
    width: 100dvw;
    left: 0;
    transform: none;
    top: 0;

    /* Portrait fullscreen: contain by width, centered */
    ${VideoContainer}.is-fullscreen:not(.is-landscape) & {
      position: static;
      display: block;
      aspect-ratio: 16 / 9;
      width: 100dvw;
      height: auto;
      max-height: var(--cdx-fs-h);
      margin: 0 auto;
      left: auto;
      transform: none;
    }

    /* Portrait interaction: keep contain-by-width, centered */
    ${VideoContainer}.is-interact:not(.is-landscape) & {
      position: static;
      display: block;
      aspect-ratio: 16 / 9;
      width: 100dvw;
      height: auto;
      max-height: var(--cdx-fs-h);
      margin: 0 auto;
      left: auto;
      transform: none;
    }
  }
`;

const IframeWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  ${VideoContainer}.is-fullscreen & {
    top: 0;
    height: var(--cdx-fs-h);
    width: 100vw;
    width: 100dvw; /* dynamic viewport width */
    display: flex;
    align-items: center;
    justify-content: center;
  }
  ${VideoContainer}.is-landscape & {
    top: 0;
    height: var(--cdx-fs-h);
    width: 100vw;
    width: 100dvw; /* dynamic viewport width */
    display: flex;
    align-items: center;
    justify-content: center;
  }
  ${VideoContainer}.is-interact & {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  ${VideoContainer}:not(.is-landscape) & {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  @media (max-width: 768px) {
    top: 0; /* use full overlay height; close button floats */
    height: calc(100vh - var(--cdx-overlay-top, 0px) - var(--cdx-overlay-bottom, 0px));
    height: calc(100dvh - var(--cdx-overlay-top, 0px) - var(--cdx-overlay-bottom, 0px));
  }
`;

const HeaderBar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 56px;
  background: rgba(0, 0, 0, 0.9);
  display: none;
  align-items: center;
  justify-content: flex-end;
  padding: 0 12px;
  z-index: 5002;

  @media (max-width: 768px) {
    display: none; /* hide bar on mobile; we use a floating X */
  }
`;

const FloatingCloseButton = styled.button<{ $fs?: boolean; $land?: boolean }>`
  position: fixed;
  top: ${props => props.$fs ? (props.$land ? '50%' : '8px') : 'calc(var(--cdx-overlay-top, 0px) + 8px)'};
  right: 8px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid #FFD700;
  background: rgba(0, 0, 0, 0.75);
  color: #FFD700;
  font-size: 24px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5003;
  box-shadow: 0 4px 10px rgba(0,0,0,0.4);
  pointer-events: auto;
  margin-top: ${props => props.$fs && props.$land ? '-24px' : '0'};

  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.06);
  }

  @media (max-width: 768px) {
    display: flex;
  }

  /* Also show whenever the container is fullscreen, even if width > 768px */
  ${VideoContainer}.is-fullscreen & {
    display: flex;
  }
`;

const HeaderCloseButton = styled.button`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 2px solid #FFD700;
  background: rgba(0, 0, 0, 0.7);
  color: #FFD700;
  font-size: 24px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1003;

  &:hover {
    background: rgba(0, 0, 0, 0.85);
    transform: scale(1.05);
  }
`;

const VideoPlayer: React.FC<VideoPlayerProps> = ({ videoId, autoplay = false, className, onClose, requestFullscreenOnMount }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    try { if (window.matchMedia && window.matchMedia('(max-width: 768px)').matches) return true; } catch {}
    try { if (/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) return true; } catch {}
    try { return window.innerWidth <= 768; } catch { return false; }
  });
  const [isFs, setIsFs] = React.useState(false);
  const [isLandscape, setIsLandscape] = React.useState(false);
  const [isInteracting, setIsInteracting] = React.useState(false);
  const interactTimer = React.useRef<number | undefined>(undefined);
  const didUnmuteRef = React.useRef(false);

  useEffect(() => {
    // Détecter/mettre à jour mobile sur mount (et si dimensions évoluent très tôt)
    const update = () => {
      let m = false;
      try { m = !!(window.matchMedia && window.matchMedia('(max-width: 768px)').matches); } catch {}
      if (!m) { try { m = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent); } catch {} }
      if (!m) { try { m = window.innerWidth <= 768; } catch {} }
      setIsMobile(m);
    };
    update();
  }, []);

  // Track orientation (portrait/landscape)
  useEffect(() => {
    const update = () => {
      try {
        const mq = window.matchMedia('(orientation: landscape)');
        setIsLandscape(!!mq.matches);
      } catch {
        const d: any = screen as any;
        const ang = (d.orientation && typeof d.orientation.angle === 'number') ? d.orientation.angle : ((window.orientation as any) || 0);
        setIsLandscape(ang === 90 || ang === -90);
      }
    };
    update();
    window.addEventListener('orientationchange', update as any);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('orientationchange', update as any);
      window.removeEventListener('resize', update);
    };
  }, []);

  // Auto-show "interaction" mode briefly on mount in portrait so sizing matches the post-tap state
  useEffect(() => {
    if (!isMobile) return;
    if (!isLandscape) {
      setIsInteracting(true);
      // try to unmute and play shortly after mount; if browser blocks, on first user touch we retry
      const u1 = window.setTimeout(() => {
        ytCmd('unMute');
        ytCmd('setVolume', [100]);
        ytCmd('playVideo');
      }, 350);
      const u2 = window.setTimeout(() => {
        ytCmd('unMute');
        ytCmd('setVolume', [100]);
        ytCmd('playVideo');
      }, 1200);
      const t = window.setTimeout(() => setIsInteracting(false), 2500);
      return () => window.clearTimeout(t);
    }
  }, [isMobile, isLandscape]);

  // Auto-set a precise fullscreen height from visualViewport (better across devices/bars)
  useEffect(() => {
    if (!isMobile) return;
    const el = containerRef.current;
    if (!el) return;
    const updateVh = () => {
      const vh = Math.round((window.visualViewport?.height || window.innerHeight));
      try { el.style.setProperty('--cdx-fs-h', `${vh}px`); } catch {}
    };
    updateVh();
    window.visualViewport?.addEventListener('resize', updateVh);
    window.addEventListener('orientationchange', updateVh);
    window.addEventListener('resize', updateVh);
    return () => {
      window.visualViewport?.removeEventListener('resize', updateVh);
      window.removeEventListener('orientationchange', updateVh);
      window.removeEventListener('resize', updateVh);
    };
  }, [isMobile]);

  // Auto-focus the iframe on load and after orientation changes so YouTube UI is ready
  useEffect(() => {
    if (!isMobile) return;
    const focusIframe = () => {
      try { iframeRef.current?.focus(); } catch {}
    };
    const i = iframeRef.current;
    const onLoad = () => setTimeout(focusIframe, 250);
    if (i) i.addEventListener('load', onLoad);
    const t = window.setTimeout(focusIframe, 500);
    window.addEventListener('orientationchange', focusIframe);
    return () => {
      if (i) i.removeEventListener('load', onLoad);
      window.removeEventListener('orientationchange', focusIframe);
      window.clearTimeout(t);
    };
  }, [isMobile, isLandscape, videoId]);

  // Ensure max volume and correct mute state when the YouTube player signals ready
  useEffect(() => {
    const onMsg = (ev: MessageEvent) => {
      try {
        const data = typeof ev.data === 'string' ? JSON.parse(ev.data) : ev.data;
        if (data && data.event === 'onReady') {
          // Always set volume to 100 on ready
          ytCmd('setVolume', [100]);
          // Keep autoplay mobile muted; otherwise ensure unmuted
          if (!(isMobile && autoplay)) {
            ytCmd('unMute');
          }
        }
      } catch {}
    };
    window.addEventListener('message', onMsg as any);
    return () => window.removeEventListener('message', onMsg as any);
  }, [isMobile, autoplay]);

  useEffect(() => {
    const handleConsent = (e: CustomEvent<any>) => {
      if (e.detail && e.detail.event === 'consent_status') {
        const iframe = iframeRef.current;
        if (iframe && iframe.parentNode) {
          // Forcer le rechargement de l'iframe après le consentement
          const src = iframe.src;
          iframe.src = 'about:blank';
          setTimeout(() => {
            iframe.src = src;
          }, 100);
        }
      }
    };

    window.addEventListener('ucEvent', handleConsent as EventListener);

    return () => {
      window.removeEventListener('ucEvent', handleConsent as EventListener);
    };
  }, [videoId]);

  // Plein écran élément: permet d'afficher le bouton X au-dessus même en fullscreen
  useEffect(() => {
    const onFsChange = () => {
      const d: any = document as any;
      const fsEl = document.fullscreenElement || d.webkitFullscreenElement || d.mozFullScreenElement || d.msFullscreenElement;
      setIsFs(!!fsEl && (fsEl === containerRef.current));
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange as any);
    document.addEventListener('mozfullscreenchange', onFsChange as any);
    document.addEventListener('MSFullscreenChange', onFsChange as any);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange as any);
      document.removeEventListener('mozfullscreenchange', onFsChange as any);
      document.removeEventListener('MSFullscreenChange', onFsChange as any);
    };
  }, []);

  // Helper to request container fullscreen programmatically
  const requestContainerFs = React.useCallback((): Promise<boolean> => {
    try {
      const el = containerRef.current as any;
      if (!el) return Promise.resolve(false);
      const rfs = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen;
      if (typeof rfs === 'function') {
        const p = rfs.call(el);
        return Promise.resolve(p).then(() => true).catch(() => false);
      }
    } catch {}
    return Promise.resolve(false);
  }, []);

  useEffect(() => {
    if (!isMobile || !requestFullscreenOnMount) return;
    requestContainerFs();
  }, [isMobile, requestFullscreenOnMount]);

  // Toggle interaction mode to ensure full YouTube UI is visible while user interacts
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !isMobile) return;

    const onInteract = () => {
      setIsInteracting(true);
      if (interactTimer.current) {
        window.clearTimeout(interactTimer.current);
      }
      interactTimer.current = window.setTimeout(() => {
        setIsInteracting(false);
      }, 3000);

      // Auto-unmute on first interaction (required by mobile autoplay policies)
      if (!didUnmuteRef.current) {
        didUnmuteRef.current = true;
        ytCmd('unMute');
        ytCmd('setVolume', [100]);
        ytCmd('playVideo');
      }
    };

    el.addEventListener('pointerdown', onInteract, { passive: true } as any);
    el.addEventListener('touchstart', onInteract as any, { passive: true } as any);
    el.addEventListener('mousemove', onInteract as any, { passive: true } as any);

    return () => {
      el.removeEventListener('pointerdown', onInteract as any);
      el.removeEventListener('touchstart', onInteract as any);
      el.removeEventListener('mousemove', onInteract as any);
      if (interactTimer.current) {
        window.clearTimeout(interactTimer.current);
      }
    };
  }, [isMobile]);

  const handleClose = () => {
    const d: any = document as any;
    const exitFs = document.exitFullscreen || d.webkitExitFullscreen || d.mozCancelFullScreen || d.msExitFullscreen;
    // Try to unlock orientation if supported
    try {
      const o: any = (screen as any).orientation;
      if (o && typeof o.unlock === 'function') {
        o.unlock();
      }
    } catch {}
    if (typeof exitFs === 'function') {
      try { exitFs.call(document); } catch {}
    }
    if (typeof onClose === 'function') {
      onClose();
    }
  };

  const pageOrigin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : 'https://cinedelux.com';
  const pageHref = (typeof window !== 'undefined' && window.location && window.location.href) ? window.location.href : 'https://cinedelux.com/';
  const baseParams = `autoplay=${autoplay ? 1 : 0}&mute=${(isMobile && autoplay) ? 1 : 0}&rel=0&modestbranding=1&playsinline=1&fs=${isMobile ? 0 : 1}`;
  const extraParams = `&hl=fr&widget_referrer=${encodeURIComponent(pageHref)}`;
  const apiParams = `&enablejsapi=1&origin=${encodeURIComponent(pageOrigin)}`;
  const embedHost = isMobile ? 'www.youtube.com' : 'www.youtube-nocookie.com';
  const embedUrl = `https://${embedHost}/embed/${videoId}?${baseParams}${extraParams}${apiParams}`;

  // Helper to control the YouTube iframe via postMessage
  const ytCmd = (func: string, args: any[] = []) => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      iframe.contentWindow?.postMessage(
        JSON.stringify({ event: 'command', func, args }),
        '*'
      );
    } catch {}
  };

  return (
    <VideoContainer className={`${className || ''} ${isFs ? 'is-fullscreen' : ''} ${isLandscape ? 'is-landscape' : ''} ${isInteracting ? 'is-interact' : ''}`} ref={containerRef}>
      <IframeWrapper>
        <StyledIframe
          ref={iframeRef}
          id="cdx-yt-iframe"
          src={embedUrl}
          title="YouTube video player"
          tabIndex={-1}
          data-fullscreen-player="1"
          data-usercentrics="YouTube"
          referrerPolicy={isMobile ? 'unsafe-url' : 'origin-when-cross-origin'}
          loading="eager"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
          allowFullScreen
        />
      </IframeWrapper>
      <HeaderBar>
        <HeaderCloseButton onClick={handleClose} aria-label="Fermer la vidéo">×</HeaderCloseButton>
      </HeaderBar>
      <RotateButton
        $fs={isFs}
        $land={isLandscape}
        onClick={async () => {
          const orient: any = (screen as any).orientation;
          if (!isFs) {
            await requestContainerFs();
          }
          try {
            if (orient && typeof orient.lock === 'function') {
              await orient.lock(isLandscape ? 'portrait-primary' : 'landscape');
            }
          } catch {}
        }}
        aria-label={isLandscape ? 'Revenir en portrait' : 'Basculer en paysage'}
        title={isLandscape ? 'Revenir en portrait' : 'Basculer en paysage'}
      >{isLandscape ? '↺' : '↻'}</RotateButton>
      <FloatingCloseButton $fs={isFs} $land={isLandscape} onClick={handleClose} aria-label="Fermer la vidéo">×</FloatingCloseButton>
    </VideoContainer>
  );
};

export default VideoPlayer;
